import React from 'react';
import { Box, Typography, Button, Paper, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ComputerIcon from '@mui/icons-material/Computer';
import CalculateIcon from '@mui/icons-material/Calculate';
import WorkIcon from '@mui/icons-material/Work';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TimelineIcon from '@mui/icons-material/Timeline';
import SpeedIcon from '@mui/icons-material/Speed';
import GraystoneGroupLogo from '../components/GraystoneGroupLogo';

const MotionTypography = motion(Typography);

const Home: React.FC = () => {
  const navigate = useNavigate();

  const solutions = [
    {
      title: 'Graystone',
      description: 'Relationship-driven recruiting firm wholly dedicated to delivering best-in-class talent services to small, mid-sized and enterprise organizations within the Private Sector.',
      capabilities: [
        {
          icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
          title: 'Cyber Security',
        },
        {
          icon: <ComputerIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
          title: 'Technology',
        },
        {
          icon: <CalculateIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
          title: 'Finance & Accounting',
        },
        {
          icon: <WorkIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
          title: 'Operations',
        },
      ],
      path: '/commercial',
      linkText: 'Explore Graystone',
    },
    {
      title: 'Graystone Federal',
      description: 'Mission-focused professional services firm driving technology implementation, modernization & securitization within the Public Sector.',
      capabilities: [
        {
          icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
          title: 'Cyber Security',
        },
        {
          icon: <StorageIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
          title: 'Infrastructure',
        },
        {
          icon: <CodeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
          title: 'Software/Product',
        },
        {
          icon: <DataObjectIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
          title: 'Data',
        },
      ],
      path: '/federal',
      linkText: 'Explore Graystone Federal',
    },

  ];

  const values = [
    {
      title: 'Transparency',
      icon: <VerifiedUserIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Clear and open communication in all our interactions.',
    },
    {
      title: 'Trust',
      icon: <HandshakeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Building lasting relationships through reliability and integrity.',
    },
    {
      title: 'Consistency',
      icon: <TimelineIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Delivering dependable results and standardized excellence.',
    },
    {
      title: 'Predictability',
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Ensuring reliable outcomes through proven processes.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          textAlign: 'center',
          pt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 2, sm: 4 },
          color: 'text.primary',
          overflow: 'hidden',
          overflowX: 'hidden',
          background: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(7, 7, 7, 0.55)), url('/images/our-mission.jpg') center/cover`,
          minHeight: { xs: 200, sm: 240, md: 280 },
          mt: 0,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justtifyContent: 'center', 
        }}
      >
        <Box
          sx={{ width: '40vw', minWidth: 360, maxWidth: 360, alignSelf: 'center',}}
          >
          <GraystoneGroupLogo />
        </Box>
        <MotionTypography 
          variant="h7" 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          sx={{ 
            mb: 4,
            fontSize: { xs: '1.0rem', sm: '1.25rem', md: '1.5rem' },
            // maxWidth: { xs: '100%', sm: '80%', md: '60%' },
            margin: '12px',
            lineHeight: 1.4,
            position: 'relative',
            zIndex: 2,
            color: 'rgba(255,255,255,0.85)',
            textShadow: '0 2px 8px rgba(0,0,0,0.25)',
            fontWeight: 300,
            fontStyle: 'italic',
          }}
        >
          Where talent drives growth and innovation across the public and private sectors
        </MotionTypography>
      </Box>

      {/* Solutions Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 4, md: 8 } }}>
        <Box sx={{ maxWidth: 'xl', mx: 'auto', px: { xs: 2, sm: 4 } }}>
          <Grid container spacing={4}>
            {solutions.map((solution, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      transition: 'transform 0.3s ease-in-out',
                    },
                  }}
                >
                  <Typography 
                    variant="h3" 
                    gutterBottom 
                    sx={{ 
                      fontSize: { xs: '1.75rem', md: '2rem' },
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    {solution.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}
                  >
                    {solution.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {solution.capabilities.map((capability, idx) => (
                      <Box 
                        key={idx} 
                        sx={{ 
                          textAlign: 'center',
                          minWidth: '120px',
                          mb: 2
                        }}
                      >
                        {capability.icon}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mt: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500
                          }}
                        >
                          {capability.title}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate(solution.path)}
                    sx={{ mt: 'auto', alignSelf: 'center' }}
                  >
                    {solution.linkText}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>


      {/* Mission Statement & Core Values Section */}
      <Box sx={{ py: { xs: 4, md: 8 } }}>
        <Box sx={{ maxWidth: 'lg', mx: 'auto', px: { xs: 2, sm: 4 } }}>
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 6,
            }}
          >
            Mission Statement
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ mb: 8, textAlign: 'center' }}
          > 
            Our mission is to intimately partner with and thoroughly understand the needs and preferences of our candidates, clients, and employees to ensure we uniformly deliver an exceptional recruitment service guided by our core values.
          </Typography>
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 6,
            }}
          >
            Our Core Values
          </Typography>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  {value.icon}
                  <Typography variant="h6" sx={{ my: 2 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Call to Action */}
      <Box
        sx={{
          textAlign: 'center',
          py: { xs: 4, sm: 6 },
          px: { xs: 2, sm: 4 },
          bgcolor: 'background.paper',
          borderRadius: 2,
          mt: 4,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Typography 
          variant="h4" 
          component="h2" 
          gutterBottom
          sx={{
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
            fontWeight: 600,
          }}
        >
          Ready to Transform Your Future?
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 4,
            fontSize: { xs: '1rem', sm: '1.1rem' },
            maxWidth: { xs: '100%', sm: '80%', md: '60%' },
            margin: '0 auto',
          }}
        >
          Partner with a firm that converts people strategy into organizational excellence.
        </Typography>
        <Button
          onClick={() => navigate('/contact')}
          variant="contained"
          color="primary"
          size="large"
          sx={{
            px: { xs: 3, sm: 4 },
            py: { xs: 1, sm: 1.5 },
            fontSize: { xs: '1rem', sm: '1.1rem' },
            mt: { xs: 3, sm: 4 }
          }}
        >
          Contact Us Today
        </Button>
      </Box>
    </Box>
  );
};

export default Home; 